import { BOOKING_STATUS_LABELS, bookingStatusSchema } from '@repo/shared-types';

import type { BookingStatus } from '@repo/shared-types';

/**
 * Presentation for a booking's status in one place — the drawer, the calendar,
 * the call detail card and the filters all read from here so a new status
 * cannot show up styled as "some other status" in one of them.
 *
 * Status is free text in the column, so anything unrecognised (a legacy row,
 * a value written by hand) falls back to the neutral tone rather than being
 * mistaken for active.
 */
export interface BookingStatusTone {
  /** Soft badge used in the drawer: tinted background + coloured text. */
  badge: string;
  /** Dot rendered inside that badge. */
  dot: string;
  /** Higher-contrast chip used on the call detail cards. */
  chip: string;
  /** FullCalendar event colours. */
  calendar: { bg: string; border: string; text: string };
  /** Whether the title should read as struck-through/void. */
  struck: boolean;
}

const TONES: Record<BookingStatus, BookingStatusTone> = {
  active: {
    badge: 'bg-emerald-500/10 text-emerald-500',
    dot: 'bg-emerald-500',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    calendar: {
      bg: 'rgba(99, 102, 241, 0.1)',
      border: 'rgb(99, 102, 241)',
      text: 'rgb(67, 56, 202)',
    },
    struck: false,
  },
  cancelled: {
    badge: 'bg-red-500/10 text-red-500',
    dot: 'bg-red-500',
    chip: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    calendar: {
      bg: 'rgba(239, 68, 68, 0.08)',
      border: 'rgb(239, 68, 68)',
      text: 'rgb(185, 28, 28)',
    },
    struck: true,
  },
  rescheduled: {
    badge: 'bg-amber-500/10 text-amber-500',
    dot: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    calendar: {
      bg: 'rgba(245, 158, 11, 0.12)',
      border: 'rgb(245, 158, 11)',
      text: 'rgb(180, 83, 9)',
    },
    struck: false,
  },
};

const NEUTRAL_TONE: BookingStatusTone = {
  badge: 'bg-gray-500/10 text-gray-400',
  dot: 'bg-gray-400',
  chip: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  calendar: {
    bg: 'rgba(107, 114, 128, 0.1)',
    border: 'rgb(107, 114, 128)',
    text: 'rgb(55, 65, 81)',
  },
  struck: false,
};

export const asBookingStatus = (status: string | null | undefined): BookingStatus | null => {
  const parsed = bookingStatusSchema.safeParse(status);
  return parsed.success ? parsed.data : null;
};

export const bookingStatusTone = (status: string | null | undefined): BookingStatusTone => {
  const known = asBookingStatus(status);
  return known ? TONES[known] : NEUTRAL_TONE;
};

export const bookingStatusLabel = (status: string | null | undefined): string => {
  const known = asBookingStatus(status);
  return known ? BOOKING_STATUS_LABELS[known] : (status ?? 'Unknown');
};
